import React, { useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/config';
import './NewsletterSignupForm.css';

interface NewsletterSignupFormProps {
  onSuccess: () => void;
  onDismiss?: () => void;
  source?: 'popup' | 'footer' | 'inline';
  compact?: boolean;
}

const NewsletterSignupForm: React.FC<NewsletterSignupFormProps> = ({
  onSuccess,
  onDismiss,
  source = 'popup',
  compact = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; general?: string }>({});
  const [isSubscribed, setIsSubscribed] = useState(false);

  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!compact && !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const response = await axios.post(`${BACKEND_URL}/business-tips/subscribe`, {
        ...formData,
        frequency: 'weekly', // Always weekly
        source,
        resubscribe: localStorage.getItem('businessTipsUnsubscribed') === 'true'
      });

      if (response.data.success) {
        localStorage.setItem('businessTipsSubscribed', 'true');
        if (localStorage.getItem('businessTipsUnsubscribed')) {
          localStorage.removeItem('businessTipsUnsubscribed');
        }
        setIsSubscribed(true);
        setFormData({ name: '', email: '' });
        
        // Auto-reset success state after 5 seconds for inline forms
        if (source !== 'popup') {
          setTimeout(() => {
            setIsSubscribed(false);
          }, 5000);
        }
        
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to subscribe. Please try again.';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (compact) {
    // Compact version for footer
    if (isSubscribed) {
      return (
        <div className="newsletter-form-compact newsletter-success">
          <div className="newsletter-compact-header">
            <h4>✅ Successfully Subscribed!</h4>
            <p>You'll receive weekly tips in your inbox</p>
          </div>
        </div>
      );
    }
    
    return (
      <form className="newsletter-form-compact" onSubmit={handleSubmit}>
        <div className="newsletter-compact-header">
          <h4>Stay Updated</h4>
          <p>Get news and updates about our CRM features</p>
        </div>
        <div className="newsletter-compact-input-group">
          <input
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            disabled={loading}
            className={errors.email ? 'error' : ''}
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Subscribing...' : 'Subscribe'}
          </button>
        </div>
        {errors.email && <span className="error-text">{errors.email}</span>}
        {errors.general && <span className="error-text">{errors.general}</span>}
      </form>
    );
  }

  // Full version for popup
  return (
    <div className="newsletter-form">
      <div className="newsletter-form-header">
        <h2>Stay Informed</h2>
        <p>Get updates about new features and improvements to our CRM</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Your Name</label>
          <input
            type="text"
            id="name"
            placeholder="John Doe"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={errors.name ? 'error' : ''}
            disabled={loading}
          />
          {errors.name && <span className="error-text">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={errors.email ? 'error' : ''}
            disabled={loading}
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        {errors.general && (
          <div className="error-message">{errors.general}</div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Subscribing...' : 'Subscribe for Updates'}
          </button>
          {source === 'popup' && onDismiss && (
            <button 
              type="button" 
              className="btn-secondary"
              onClick={onDismiss}
              disabled={loading}
            >
              Maybe Later
            </button>
          )}
        </div>
      </form>

      <p className="privacy-note">
        We respect your privacy. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default NewsletterSignupForm;